import { useState } from 'react'
import { CalendarDays, Wallet, UserSearch, CheckCircle2 } from 'lucide-react'
import { ChartCard, CurrencyValue, PermissionGate } from '@/components/common'
import { Skeleton } from '@/components/ui/skeleton'
import { PeriodSelect } from './PeriodSelect'
import { useLeaveSummary, usePayrollSummary, useRecruitmentSummary } from '../api'
import { cn } from '@/lib/utils'

const THIS_MONTH = [{ value: 'month', label: 'This Month' }]

/** A label/value pair, as used by all three summary cards. */
function Row({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-center justify-between border-b border-[var(--color-border)] py-2.5 text-sm last:border-b-0">
      <span className="text-[var(--color-body)]">{label}</span>
      <span className={cn('font-semibold tabular-nums text-[var(--color-heading)]', valueClass)}>{value}</span>
    </div>
  )
}

function RowsSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3 py-2">
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className="h-5 w-full" />
      ))}
    </div>
  )
}

export function LeaveSummaryCard() {
  const [period, setPeriod] = useState('month')
  const { data, isLoading } = useLeaveSummary(period)
  const format = new Intl.NumberFormat('en')

  return (
    <ChartCard
      title="Leave Summary"
      icon={CalendarDays}
      filter={<PeriodSelect value={period} options={THIS_MONTH} onChange={setPeriod} label="Leave period" />}
      footerLink={{ label: 'View Leave Report', to: '/leave' }}
    >
      {isLoading && <RowsSkeleton />}
      {data && (
        <div className="py-1">
          <Row label="Total Leave" value={format.format(data.total)} />
          <Row label="Approved" value={format.format(data.approved)} valueClass="text-[var(--color-success)]" />
          <Row label="Pending" value={format.format(data.pending)} valueClass="text-[var(--color-warning)]" />
          <Row label="Rejected" value={format.format(data.rejected)} valueClass="text-[var(--color-danger)]" />
        </div>
      )}
    </ChartCard>
  )
}

/** Human labels for the payroll run states. */
const PAYROLL_STATUS: Record<string, string> = {
  NOT_STARTED: 'Not started',
  DRAFT: 'Draft',
  PROCESSING: 'Processing',
  APPROVED: 'Approved',
  PAID: 'Paid',
}

export function PayrollSummaryCard() {
  const [period, setPeriod] = useState('')
  const { data, isLoading } = usePayrollSummary(period || undefined)
  const format = new Intl.NumberFormat('en')

  const periodOptions = data
    ? [{ value: '', label: `${String(data.month).padStart(2, '0')}/${data.year}` }]
    : [{ value: '', label: 'Current' }]

  return (
    <ChartCard
      title="Payroll Summary"
      icon={Wallet}
      filter={<PeriodSelect value={period} options={periodOptions} onChange={setPeriod} label="Payroll period" />}
      footerLink={{ label: 'View Payroll Dashboard', to: '/payroll' }}
    >
      {isLoading && <RowsSkeleton rows={3} />}
      {data && (
        <div className="py-1">
          <div className="rounded-[var(--radius-control)] bg-[var(--color-tint-green)] px-3 py-3 text-center">
            <p className="flex items-center justify-center gap-1.5 text-xs font-medium text-[var(--color-success)]">
              <CheckCircle2 className="size-3.5" aria-hidden="true" />
              Payroll Status
            </p>
            <p className="mt-0.5 text-base font-semibold text-[var(--color-heading)]">
              {PAYROLL_STATUS[data.status] ?? data.status}
            </p>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 text-center">
            <div>
              <p className="text-xs text-[var(--color-body)]">Total Employees</p>
              <p className="mt-0.5 font-semibold tabular-nums text-[var(--color-heading)]">
                {format.format(data.totalEmployees)}
              </p>
            </div>
            <div>
              <p className="text-xs text-[var(--color-body)]">Total Payroll Cost</p>
              <p className="mt-0.5 font-semibold text-[var(--color-heading)]">
                <CurrencyValue value={data.totalPayrollCost} currency={data.currency} compact />
              </p>
            </div>
          </div>
        </div>
      )}
    </ChartCard>
  )
}

export function RecruitmentSummaryCard() {
  const [period, setPeriod] = useState('month')
  const { data, isLoading } = useRecruitmentSummary(period)
  const format = new Intl.NumberFormat('en')

  return (
    <ChartCard
      title="Recruitment Summary"
      icon={UserSearch}
      filter={<PeriodSelect value={period} options={THIS_MONTH} onChange={setPeriod} label="Recruitment period" />}
      footerLink={{ label: 'View Recruitment Dashboard', to: '/recruitment' }}
    >
      {isLoading && <RowsSkeleton />}
      {data && (
        <div className="py-1">
          <Row label="Total Openings" value={format.format(data.totalOpenings)} />
          <Row label="Active Candidates" value={format.format(data.activeCandidates)} />
          <Row label="Interviews Scheduled" value={format.format(data.interviewsScheduled)} />
          <Row label="Offers Sent" value={format.format(data.offersSent)} />
        </div>
      )}
    </ChartCard>
  )
}

/** Wraps each summary card in the permission its endpoint requires. */
export function SummaryCardsRow() {
  return (
    <>
      <PermissionGate permission="leave:read">
        <LeaveSummaryCard />
      </PermissionGate>
      <PermissionGate permission="payroll:read">
        <PayrollSummaryCard />
      </PermissionGate>
      <PermissionGate permission="recruitment:read">
        <RecruitmentSummaryCard />
      </PermissionGate>
    </>
  )
}
